import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Select,
  Spinner,
  Stack,
  Tag,
} from '@chakra-ui/react';
import { useInvestorAccountModel } from '@common/models/InvestorAccountModel';
import { ExitTimingValues } from '@common/models/InvestorAccountModel/types';
import { ExitTimingMapping } from '@services/appsync/mappings';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

export const ExitTimingForm: React.FC = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>();
  const { investorAccount } = useInvestorAccountModel();
  const survey = investorAccount?.non_accredited_survey_v2 ?? null;
  const { updateTimeHorizon } = useInvestorAccountModel();

  const form = useForm<ExitTimingValues>({
    defaultValues: {
      timeHorizon: survey?.time_horizon?.value,
    },
  });

  const save = form.handleSubmit(async (data) => {
    setIsSaving(true);
    setFormError(null);
    const result = await updateTimeHorizon(data);
    if (result.error) {
      form.reset();
      setIsSaving(false);
      setFormError(result.error);
    } else {
      form.reset(data);
      setIsSaving(false);
    }
  });

  const needsAttention = !survey?.time_horizon?.value;

  return (
    <Grid templateColumns={['1fr 1fr']} gap={25} w="100%">
      <GridItem colSpan={2} display="flex" flexDirection="row" justifyContent="space-between">
        <Stack direction={['column', 'row']} spacing={[2, 5]}>
          <Heading id="exit-timing" fontSize={'21px'}>
            Exit Timing
          </Heading>
          {needsAttention && <Tag colorScheme={'yellow'}>Needs Attention</Tag>}
        </Stack>
        {form.formState.isDirty &&
          (isSaving ? (
            <Spinner color="blue.500" size="md" thickness="4px" />
          ) : (
            <HStack spacing={5}>
              <Button onClick={save}>Save</Button>
              <Button variant="link" onClick={() => form.reset()}>
                Cancel
              </Button>
            </HStack>
          ))}
      </GridItem>
      {formError && (
        <GridItem colSpan={2}>
          <Alert status="error">
            <AlertIcon />
            {formError}
          </Alert>
        </GridItem>
      )}
      <GridItem colSpan={2} borderTopWidth={1} borderTopColor={'gray.50'} />

      <GridItem colSpan={[2, 1]}>
        <FormControl isInvalid={Boolean(form.formState.errors.timeHorizon)}>
          <FormLabel htmlFor="exitTiming">On what time scale are your looking to exit your investments?</FormLabel>
          <Select
            isDisabled={isSaving}
            placeholder="Select option"
            {...form.register('timeHorizon', {
              validate: (value) => {
                if (!value) {
                  return 'Exit timing is required';
                } else {
                  return true;
                }
              },
            })}>
            <option value={ExitTimingMapping.ONE_TO_TWO_YEARS}>1-2 Years</option>
            <option value={ExitTimingMapping.THREE_TO_FOUR_YEARS}>3-4 Years</option>
            <option value={ExitTimingMapping.FIVE_TO_TEN_YEARS}>5-10 Years</option>
            <option value={ExitTimingMapping.OVER_10_YEARS}>10+ Years</option>
          </Select>
          <FormErrorMessage>
            {form.formState.errors.timeHorizon && form.formState.errors.timeHorizon?.message}
          </FormErrorMessage>
        </FormControl>
      </GridItem>
    </Grid>
  );
};
